import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { showAlert } from '../../../../../utils/alerts';
import FinalizeTable from './table';
import { axios } from '../../../../../utils/axios';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest';
import { transformData } from './helper';
import moment from 'moment';
import { crRoles } from '../../../../../constants';

const FinalizeTPFlow = ({ activeTab, activeSubTab, setLogsModal }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { crNumber } = useParams();
  const { getIntCrUserRoleMappings, userRoleMappings, isCrRoleAuthorized, info, crAssignedSpocs } =
    useContext(CostingRequestsContext);
  const [syncDetails, setSyncDetails] = useState(null);
  const syncIntervalRef = useRef(null);
  const { producing_plant_data } = info;

  useEffect(() => {
    const fetchMappings = async () => {
      await getIntCrUserRoleMappings();
    };
    fetchMappings();
  }, []);

  useEffect(() => {
    if (
      activeTab === 'tpSetup' &&
      activeSubTab === 'finalizeTpFlow' &&
      producing_plant_data.length > 0
    ) {
      fetch();
      fetchSyncTime();
    }
  }, [activeTab, activeSubTab, producing_plant_data]);

  useEffect(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }

    if (!syncDetails?.nextSyncAt) return;

    const checkSync = () => {
      const currentTime = moment().utc();
      const nextSyncTime = moment(syncDetails.nextSyncAt);

      if (nextSyncTime.isSameOrBefore(currentTime)) {
        fetch();
        fetchSyncTime();
      }
    };

    syncIntervalRef.current = setInterval(checkSync, 30000);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [syncDetails?.nextSyncAt]);

  useEffect(() => {
    const handleUnload = () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []);

  const getAllowedCountry = (type) => {
    try {
      if (type === 'logistics') {
        let logistics = userRoleMappings
          .filter(
            (role) =>
              role.country && role.is_active && role.int_role?.alias === 'logistics_fpna_analyst',
          )
          .map((role) => role.country);

        logistics = [...new Set(logistics)];
        return logistics;
      }

      let countries = userRoleMappings
        .filter((role) => role.country && role.is_active && role.int_role?.name === 'GCC FP&A')
        .map((role) => role.country);

      countries = [...new Set(countries)];
      return countries;
    } catch (e) {
      console.log(e);
      return [];
    }
  };
  const checkAccess = (status, country, type) => {
    const accessAllowed =
      String(status).toLowerCase() === 'in progress' &&
      isCrRoleAuthorized(
        producing_plant_data[0].producing_country,
        type === 'logistics' ? crRoles.logistics_fpna_analyst : undefined,
      );
    return accessAllowed;
  };

  const handleData = (data) => {
    const transformedData = data.flatMap((entry) =>
      entry.IntCrTpFinalizations.map((log) => ({
        ...entry,
        IntCrTpFinalizations: [log],
      })),
    );
    return transformedData;
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const api = `/costing-request/step/finalize-tp/${crNumber}`;
      const { data } = await axios.get(api);
      const allData = handleData(data?.data);
      const apiData = transformData(
        allData,
        crAssignedSpocs,
        producing_plant_data[0].producing_country,
      );
      setData(apiData);
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncTime = async () => {
    try {
      const { data } = await axios.get(`/costing-request/step/finalize_tp_logs`);

      const syncLogs = data.data || {};

      setSyncDetails({
        lastSyncAt: syncLogs?.[0]?.last_sync_time,
        nextSyncAt: moment(syncLogs?.[0]?.next_sync_time),
      });
    } catch (e) {
      console.log(e);
    }
  };
  const submitLogisticsChange = async (id, log) => {
    setLoading(true);
    const payload = {
      id: id,
      stg_log: String(log),
    };
    try {
      const res = await axios.post(`/costing-request/step/input-tp/${crNumber}`, payload);
      if (res?.data?.success) {
        showAlert({ message: 'Std Logistics Cost submitted successfully! ' }, 'success');
      }
    } catch (e) {
      showAlert({ message: 'Std Logistics Cost submission failed, Please check Logs ' }, 'error');
    } finally {
      await fetch();
      setLoading(false);
    }
  };
  const handleChange = (val) => {
    setData((prevData) => prevData.map((ele) => (ele.id === val.id ? { ...ele, ...val } : ele)));
  };
  const finalizeTp = async (id) => {
    setLoading(true);
    try {
      const api = `/costing-request/step/finalize-tp/${crNumber}`;
      const payload = { id: id };
      const res = await axios.post(api, payload);
      if (res?.data?.success) {
        showAlert({ message: 'Finalization of TP success' }, 'success');
      } else {
        showAlert({ message: 'Finalization of TP failed, Please check logs' }, 'error');
      }
    } catch (e) {
      showAlert({ message: 'Finalization of TP failed, Please check logs' }, 'error');
    } finally {
      await fetch();
      setLoading(false);
    }
  };
  const initialState = {
    columnPinning: {
      left: [],
      right: ['logs'],
    },
    sorting: [{}],
  };
  return (
    <>
      <FinalizeTable
        loading={loading}
        data={data}
        initialState={initialState}
        handleChange={handleChange}
        setLogsModal={setLogsModal}
        finalizeTp={finalizeTp}
        checkAccess={checkAccess}
        submitLogisticsChange={submitLogisticsChange}
        fetch={() => {
          fetch();
          fetchSyncTime();
        }}
        syncDetails={syncDetails}
      />
    </>
  );
};

export default FinalizeTPFlow;
